import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { CopySalespersonApplyLinksModal } from '../../components/forms/CopySalespersonApplyLinksModal';
import { ErrorOverlay } from '../../components/ui/ErrorOverlay';
import {
  fetchAccountCreationSubmissions,
  getAcraAttachmentSignedUrl,
} from '../../lib/applicationFormSubmissions';
import { formatSubmittedAt, statusClass, STATUS_LABEL } from '../../lib/accountCreationUi';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import { usePortal } from '../../contexts/PortalContext';
import type { AccountCreationFormData, ApplicationSubmission } from '../../types/applicationForm';
import { formatSalespersonCode } from '../../lib/accountCollection';

function submissionSalespersonCode(row: ApplicationSubmission): string {
  const formData = row.form_data as AccountCreationFormData | undefined;
  const raw =
    formData?.salesperson_code?.trim() ||
    row.finance_data?.salesperson_code?.trim() ||
    '';
  return formatSalespersonCode(raw);
}

export function AccountCreationListPage() {
  const { basePath } = usePortal();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ApplicationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linksOpen, setLinksOpen] = useState(false);

  const listBase = basePath ? `${basePath}/account-creation` : '/account-creation';
  const newPath = `${listBase}/new`;
  const homePath = basePath || '/forms';

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    setError(null);
    try {
      setRows(await fetchAccountCreationSubmissions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load applications.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    tables: ['account_creation_submissions'],
    onChange: () => {
      void load({ silent: true });
    },
  });

  const pendingCount = useMemo(
    () =>
      rows.filter(
        (row) => row.status === 'pending_review' || row.status === 'pending_approval',
      ).length,
    [rows],
  );

  const detailPath = (id: string) => `${listBase}/${id}`;

  const openAcraAttachment = async (event: MouseEvent, row: ApplicationSubmission) => {
    event.stopPropagation();
    const path = (row.form_data as AccountCreationFormData | undefined)?.acra_attachment_path?.trim();
    if (!path) return;
    try {
      const url = await getAcraAttachmentSignedUrl(path);
      if (!url) {
        setError('Attachment not found.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open attachment.');
    }
  };

  return (
    <div className="af-applications-page">
      {error ? <ErrorOverlay message={error} onClose={() => setError(null)} /> : null}
      <PageHeader
        title="Account Creation"
        description="New customer applications pending verification and AR approval."
        actions={
          <div className="af-applications-page__actions">
            <Link to={homePath} className="btn-secondary btn-sm">
              All forms
            </Link>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setLinksOpen(true)}>
              Copy salesperson links
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => void load()}>
              Refresh
            </button>
            <Link to={newPath} className="btn-primary btn-sm">
              New application
            </Link>
          </div>
        }
      />

      <div className="af-stat-row">
        <div className="af-stat-card">
          <span className="af-stat-card__label">Total applications</span>
          <strong className="af-stat-card__value">{rows.length}</strong>
        </div>
        <div className="af-stat-card">
          <span className="af-stat-card__label">Pending review</span>
          <strong className="af-stat-card__value">{pendingCount}</strong>
        </div>
      </div>

      <div className="card af-applications-panel">
        {loading ? (
          <p className="page-lead">Loading applications…</p>
        ) : rows.length === 0 ? (
          <div className="af-empty-state">
            <p>No applications yet.</p>
            <p className="page-lead">Create an internal application to begin.</p>
          </div>
        ) : (
          <div className="af-table-wrap">
            <table className="af-app-table">
              <thead>
                <tr>
                  <th>Ref. No.</th>
                  <th>Company</th>
                  <th>Salesperson</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Finance contact</th>
                  <th>ACRA</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const formData = row.form_data as AccountCreationFormData;
                  return (
                    <tr
                      key={row.id}
                      className="af-app-table__row"
                      tabIndex={0}
                      onClick={() => navigate(detailPath(row.id))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(detailPath(row.id));
                        }
                      }}
                    >
                      <td>
                        <Link
                          to={detailPath(row.id)}
                          className="af-app-table__link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.application_no}
                        </Link>
                      </td>
                      <td className="af-app-table__company">{row.company_name}</td>
                      <td className="mono">{submissionSalespersonCode(row) || '—'}</td>
                      <td>
                        <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
                      </td>
                      <td>
                        <span className={`af-source-badge af-source-badge--${row.source}`}>
                          {row.source === 'public' ? 'Customer' : 'Internal'}
                        </span>
                      </td>
                      <td>{formData.finance_contact_name?.trim() || '—'}</td>
                      <td>
                        {formData.acra_attachment_path ? (
                          <button
                            type="button"
                            className="af-app-table__chip"
                            onClick={(e) => void openAcraAttachment(e, row)}
                          >
                            {formData.acra_attachment_name || 'View'}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="af-app-table__date">{formatSubmittedAt(row.submitted_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CopySalespersonApplyLinksModal open={linksOpen} onClose={() => setLinksOpen(false)} />
    </div>
  );
}
